import { useEffect, useRef, useState } from 'react'
import type { StoragePart, StorageReport, StorageSide } from '@shared/services'
import { readStorage, withRetry } from '@shared/services'
import { KEPT, keep, readKept } from './usageKept'

/**
 * What the platform is storing (Huanrui, 2026-10-05)
 * -------------------------------------------------------------
 * Two cards under the people, Supabase on the left and R2 on the right, each
 * in its own maker's colour: Supabase's green, Cloudflare's yellow. One hue
 * per card, so colour only says which service; everything that has to be read
 * is in text.
 *
 * Each card: how much is held now, against what the plan includes; how it has
 * grown, one point a day (the Worker keeps a snapshot every night, and when
 * this page is opened); and what it is made of. Counted by the Worker at most
 * once an hour (workers/api/src/routes/storage.ts).
 *
 * What it cannot show -- egress, request counts, 429s -- is only in the
 * services' own dashboards, linked from each card.
 */

export interface StorageHover {
  title: string
  lines: { color: string; name: string; value: string }[]
}

const SUPABASE_DASHBOARD = 'https://supabase.com/dashboard/project/gymbhkjiyqpeaatgnbfw'
const R2_DASHBOARD = 'https://dash.cloudflare.com/?to=/:account/r2/overview'

/** Plain names for what each part is. */
const NAMES: Record<string, string> = {
  assets: 'Libraries',
  'project-files': 'Projects',
  imports: 'Imports',
  library: 'Library thumbnails',
  project: 'Project copies',
  other: 'Worker state',
}

export function bytes(n: number): string {
  if (n < 1024) return `${n} B`
  const units = ['KB', 'MB', 'GB', 'TB']
  let value = n / 1024
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  return `${value >= 100 ? value.toFixed(0) : value >= 10 ? value.toFixed(1) : value.toFixed(2)} ${units[unit]}`
}

function shortDay(day: string): string {
  const at = new Date(`${day}T12:00:00`)
  return at.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

export function StorageCards({
  onHover,
  onLeave,
}: {
  onHover: (event: React.PointerEvent, hover: StorageHover) => void
  onLeave: () => void
}) {
  // The last figures this browser saw, at once (2026-10-05); then the
  // Worker's, which are themselves the last count while it counts again.
  const [report, setReport] = useState<StorageReport | null>(() => readKept<StorageReport>(`${KEPT}:storage`))
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    let again: number | undefined
    const read = (more: boolean) =>
      withRetry(() => readStorage())
        .then((found) => {
          if (cancelled) return
          setReport(found)
          keep(`${KEPT}:storage`, found)
          // Counting again behind that answer: ask once more when it is done.
          if (found.recounting && more) again = window.setTimeout(() => void read(false), 8000)
        })
        .catch((err: unknown) => {
          if (!cancelled) setError(err instanceof Error ? err.message : 'Could not read the storage')
        })
    void read(true)
    return () => {
      cancelled = true
      window.clearTimeout(again)
    }
  }, [])

  return (
    <div className="use__store">
      <StoreCard
        provider="supabase"
        name="Supabase"
        link={SUPABASE_DASHBOARD}
        side={report?.supabase ?? null}
        allowance={report?.allowances.supabaseFiles ?? 0}
        parts={report?.supabase.parts ?? []}
        extra={
          report ? (
            <Meter
              label="Database"
              used={report.supabase.database}
              allowance={report.allowances.supabaseDatabase}
              small
            />
          ) : null
        }
        error={error}
        recounting={report?.recounting ?? false}
        onHover={onHover}
        onLeave={onLeave}
      />
      <StoreCard
        provider="r2"
        name="Cloudflare R2"
        link={R2_DASHBOARD}
        side={report?.r2 ?? null}
        allowance={report?.allowances.r2 ?? 0}
        parts={report?.r2.parts ?? []}
        allowanceNote="free"
        error={error}
        recounting={report?.recounting ?? false}
        onHover={onHover}
        onLeave={onLeave}
      />
    </div>
  )
}

function StoreCard({
  provider,
  name,
  link,
  side,
  allowance,
  allowanceNote = 'included',
  parts,
  extra,
  error,
  recounting,
  onHover,
  onLeave,
}: {
  provider: 'supabase' | 'r2'
  name: string
  link: string
  side: StorageSide | null
  allowance: number
  allowanceNote?: string
  parts: StoragePart[]
  extra?: React.ReactNode
  error: string
  recounting: boolean
  onHover: (event: React.PointerEvent, hover: StorageHover) => void
  onLeave: () => void
}) {
  const largest = Math.max(1, ...parts.map((one) => one.bytes))
  return (
    <section className={`use__store-card use__store-card--${provider}`} aria-label={`${name} storage`}>
      <div className="use__card-head">
        <span className="use__store-name">
          <i aria-hidden="true" />
          {name}
        </span>
        <span className="use__spacer" />
        <a className="use__store-link" href={link} target="_blank" rel="noreferrer">
          Dashboard ↗
        </a>
      </div>

      {side ? (
        <>
          <Meter label="Files" used={side.bytes} allowance={allowance} note={allowanceNote} count={side.objects} />
          {extra}
          <Growth provider={provider} name={name} history={side.history} onHover={onHover} onLeave={onLeave} />
          {parts.length > 0 ? (
            <div className="use__store-parts">
              {parts.map((one) => (
                <div key={one.name} className="use__store-part">
                  <span className="use__store-part-name">{NAMES[one.name] ?? one.name}</span>
                  <span className="use__store-part-count">{one.objects.toLocaleString()} files</span>
                  <b>{bytes(one.bytes)}</b>
                  <span className="use__store-part-track" aria-hidden="true">
                    <span style={{ width: `${Math.max(1.5, (one.bytes / largest) * 100)}%` }} />
                  </span>
                </div>
              ))}
            </div>
          ) : null}
          {side.at ? (
            <p className="use__store-when">
              Counted {new Date(side.at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} · {recounting ? 'counting again…' : 'again after an hour'}
            </p>
          ) : null}
        </>
      ) : (
        <p className="use__quiet">{error || 'Counting…'}</p>
      )}
    </section>
  )
}

/** How much of what the plan includes, as a number and a thin bar. */
function Meter({
  label,
  used,
  allowance,
  note = 'included',
  count,
  small = false,
}: {
  label: string
  used: number
  allowance: number
  note?: string
  count?: number
  small?: boolean
}) {
  const share = allowance > 0 ? used / allowance : 0
  return (
    <div className={`use__store-meter${small ? ' use__store-meter--small' : ''}`}>
      <div className="use__store-figures">
        {small ? <span className="use__store-meter-label">{label}</span> : null}
        <strong>{bytes(used)}</strong>
        {allowance > 0 ? (
          <span>
            of {bytes(allowance)} {note} · {(share * 100).toFixed(share < 0.1 ? 1 : 0)}%
          </span>
        ) : null}
        {typeof count === 'number' ? <span className="use__store-count">{count.toLocaleString()} files</span> : null}
      </div>
      <div
        className="use__store-track"
        role="meter"
        aria-label={`${label}: ${bytes(used)} of ${bytes(allowance)}`}
        aria-valuemin={0}
        aria-valuemax={allowance || 1}
        aria-valuenow={used}
      >
        <span className={share > 1 ? 'use__store-fill--over' : ''} style={{ width: `${Math.min(100, Math.max(share > 0 ? 0.8 : 0, share * 100))}%` }} />
      </div>
    </div>
  )
}

const CHART_HEIGHT = 72
const PAD_TOP = 8
const PAD_BOTTOM = 18

/**
 * How the total has grown: one point a day, a 2px line over a faint wash, a
 * dot at today. Hover anywhere along it for the day and the size.
 */
function Growth({
  provider,
  name,
  history,
  onHover,
  onLeave,
}: {
  provider: 'supabase' | 'r2'
  name: string
  history: { day: string; bytes: number }[]
  onHover: (event: React.PointerEvent, hover: StorageHover) => void
  onLeave: () => void
}) {
  const box = useRef<HTMLDivElement | null>(null)
  const [width, setWidth] = useState(0)
  const [at, setAt] = useState<number | null>(null)

  useEffect(() => {
    const node = box.current
    if (!node) return
    const watch = new ResizeObserver(([entry]) => setWidth(Math.floor(entry!.contentRect.width)))
    watch.observe(node)
    return () => watch.disconnect()
  }, [])

  if (history.length < 2) {
    return (
      <div className="use__store-growth" ref={box}>
        <p className="use__store-empty">One point a day from today; the line appears tomorrow.</p>
      </div>
    )
  }

  const colour = `var(--store-${provider})`
  const top = Math.max(...history.map((one) => one.bytes))
  const bottom = Math.min(...history.map((one) => one.bytes))
  // From a little under the smallest, so growth is visible; never below zero.
  const floor = Math.max(0, bottom - (top - bottom) * 0.25)
  const span = Math.max(1, top - floor)
  const inner = CHART_HEIGHT - PAD_TOP - PAD_BOTTOM
  const x = (index: number) => (width <= 0 ? 0 : 4 + (index / (history.length - 1)) * (width - 8))
  const y = (value: number) => PAD_TOP + inner - ((value - floor) / span) * inner
  const line = history.map((one, index) => `${index === 0 ? 'M' : 'L'}${x(index).toFixed(1)} ${y(one.bytes).toFixed(1)}`).join(' ')
  const wash = `${line} L${x(history.length - 1).toFixed(1)} ${PAD_TOP + inner} L${x(0).toFixed(1)} ${PAD_TOP + inner} Z`
  const last = history.length - 1

  const point = (event: React.PointerEvent<SVGRectElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    const ratio = (event.clientX - rect.left) / Math.max(1, rect.width)
    const index = Math.min(last, Math.max(0, Math.round(ratio * last)))
    setAt(index)
    const one = history[index]!
    onHover(event, {
      title: shortDay(one.day),
      lines: [{ color: colour, name, value: bytes(one.bytes) }],
    })
  }

  return (
    <div className="use__store-growth" ref={box}>
      {width > 0 ? (
        <svg width={width} height={CHART_HEIGHT} role="img" aria-label={`${name}: ${bytes(history[0]!.bytes)} on ${shortDay(history[0]!.day)}, ${bytes(history[last]!.bytes)} now`}>
          <line x1={0} x2={width} y1={PAD_TOP + inner} y2={PAD_TOP + inner} className="use__store-base" />
          <path d={wash} fill={colour} opacity={0.1} />
          <path d={line} fill="none" stroke={colour} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
          {at !== null ? (
            <>
              <line x1={x(at)} x2={x(at)} y1={PAD_TOP} y2={PAD_TOP + inner} className="use__store-cross" />
              <circle cx={x(at)} cy={y(history[at]!.bytes)} r={4} fill={colour} className="use__store-dot" />
            </>
          ) : (
            <circle cx={x(last)} cy={y(history[last]!.bytes)} r={4} fill={colour} className="use__store-dot" />
          )}
          <text x={0} y={CHART_HEIGHT - 4} className="use__store-axis">
            {shortDay(history[0]!.day)}
          </text>
          <text x={width} y={CHART_HEIGHT - 4} className="use__store-axis" textAnchor="end">
            {shortDay(history[last]!.day)}
          </text>
          <rect
            x={0}
            y={0}
            width={width}
            height={CHART_HEIGHT}
            fill="transparent"
            onPointerMove={point}
            onPointerLeave={() => {
              setAt(null)
              onLeave()
            }}
          />
        </svg>
      ) : null}
    </div>
  )
}
